import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { NOTIFICATION_TYPE_CONFIG } from '../../constants/notification-type-config';
import { Notification, NotificationType } from '../../types';
import { NotificationItemShell } from './notification-item-shell';

interface NotificationItemTimeTrackingReminderProps {
  notification: Notification;
  onMarkAsRead?: (notificationId: number) => void;
  onDelete?: (notificationId: number) => void;
}

enum ReminderBasis {
  Booked = 'booked',
  Availability = 'availability',
}

/** i18n `types.*` namespace segment for each reminder notification type. */
const REMINDER_I18N_SEGMENT: Record<string, string> = {
  [NotificationType.TIME_TRACKING_WEEKLY_REMINDER]: 'timeTrackingWeeklyReminder',
  [NotificationType.TIME_TRACKING_MONTHLY_REMINDER]: 'timeTrackingMonthlyReminder',
};

/**
 * Inbox item for the weekly/monthly time-tracking reminder notifications.
 * Adds a "by booked" / "by availability" toggle (mirroring the Week view's
 * `TotalsBasisCell`) that switches which description template renders —
 * booked total + booked percentage (`args.effectiveBooked` / `args.bookedPercentage`)
 * vs. effective capacity + availability percentage (`args.effectiveCapacity` /
 * `args.availabilityPercentage`). Both values arrive pre-formatted from the
 * backend, so this component only picks which template + args to interpolate.
 */
export function NotificationItemTimeTrackingReminder({
  notification,
  onMarkAsRead,
  onDelete,
}: NotificationItemTimeTrackingReminderProps) {
  const { t } = useTranslation('authenticated.notifications');
  const [basis, setBasis] = useState(ReminderBasis.Booked);

  const typeConfig = NOTIFICATION_TYPE_CONFIG[notification.type];
  const actionUrl = typeConfig?.getActionUrl
    ? typeConfig.getActionUrl(notification.actionArgs ?? {})
    : null;

  const i18nSegment = REMINDER_I18N_SEGMENT[notification.type];
  const args = notification.args ?? {};
  const isAvailability = basis === ReminderBasis.Availability;

  const title = typeConfig ? t(typeConfig.titleKey, args) : '';
  const description = i18nSegment
    ? t(
        `types.${i18nSegment}.${isAvailability ? 'descriptionAvailability' : 'descriptionBooked'}`,
        args
      )
    : '';

  return (
    <NotificationItemShell
      notification={notification}
      title={title}
      actionUrl={actionUrl}
      onMarkAsRead={onMarkAsRead}
      onDelete={onDelete}
    >
      <p className="whitespace-pre-line text-sm leading-snug text-muted-foreground">
        {description}
      </p>
      <Tabs
        value={basis}
        onValueChange={value => setBasis(value as ReminderBasis)}
        onClick={e => e.stopPropagation()}
      >
        <TabsList className="inline-flex h-7 w-auto self-start border border-border bg-muted p-0.5">
          <TabsTrigger value={ReminderBasis.Booked} className="px-2 py-0.5 text-[11px]">
            {i18nSegment ? t(`types.${i18nSegment}.basisBooked`) : ''}
          </TabsTrigger>
          <TabsTrigger value={ReminderBasis.Availability} className="px-2 py-0.5 text-[11px]">
            {i18nSegment ? t(`types.${i18nSegment}.basisAvailability`) : ''}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </NotificationItemShell>
  );
}
